import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import {
  ResponsiveContainer,
  ResponsiveHeader,
  ResponsiveContent,
  ResponsiveFooter,
} from '../ui/ResponsiveLayout';
import { Card, Button } from '../ui';
import { FinishFlowAction } from '../../flows/FinishFlowAction';
import { DocumentsIcon } from '../ui/Icons';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

interface Pet {
  id: string;
  name: string;
  type: 'dog' | 'cat' | 'bird' | 'fish' | 'reptile' | 'other' | '';
  breed: string;
  age: string;
  specialNeeds: string;
  veterinarian: string;
  veterinarianContact: string;
  preferredCaregiver: string;
  caregiverContact: string;
  careInstructions: string;
  diet?: string;
  medications?: string;
  emergencyContact: string;
  emergencyContactPhone: string;
  documents?: AppDocument[];
}

interface Dependant {
  id: string;
  name: string;
  relationship: 'child' | 'parent' | 'sibling' | 'spouse' | 'other' | '';
  age: string;
  specialNeeds: string;
  medicalConditions: string;
  medications: string;
  preferredGuardian: string;
  guardianContact: string;
  emergencyContact: string;
  emergencyContactPhone: string;
  careInstructions: string;
  documents?: AppDocument[];
}

interface FinancialArrangements {
  petCareBudget: string;
  petCareInstructions: string;
  dependentCareBudget: string;
  dependentCareInstructions: string;
  trustFunds: string;
  insurancePolicies: string;
  emergencyFunds: string;
  specialInstructions: string;
  paymentSchedule: string;
  careCoordinator: string;
  careCoordinatorContact: string;
}

interface FinalInstructions {
  emergencyProcedures: string;
  documentLocations: string;
  importantContacts: string;
  finalMessage: string;
  specialRequests: string;
}

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const Frame248: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const [pets, setPets] = useState<Pet[]>([]);
  const [dependents, setDependents] = useState<Dependant[]>([]);
  const [financialArrangements, setFinancialArrangements] =
    useState<FinancialArrangements | null>(null);
  const [finalInstructions, setFinalInstructions] =
    useState<FinalInstructions | null>(null);

  const [expandedPets, setExpandedPets] = useState<Record<string, boolean>>({});
  const [expandedDependents, setExpandedDependents] = useState<Record<string, boolean>>({});

  const normalizePets = (input: Pet[]): Pet[] => {
    return (input || []).map((pet) => ({
      ...pet,
      documents: normalizeDocuments(pet.documents || []),
    }));
  };

  const normalizeDependents = (input: Dependant[]): Dependant[] => {
    return (input || []).map((dependent) => ({
      ...dependent,
      documents: normalizeDocuments(dependent.documents || []),
    }));
  };

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame248] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const savePetsDependantsBackup = (nextPets: Pet[], nextDependents: Dependant[]) => {
    try {
      localStorage.setItem(
        'pets-dependants-backup',
        JSON.stringify({
          pets: nextPets,
          dependants: nextDependents,
        })
      );
    } catch (error) {
      console.error('[Frame248] Failed to save pets/dependants backup:', error);
    }
  };

  const persistPetsTask = async (nextPets: Pet[], nextDependents: Dependant[]) => {
    try {
      const currentAnswers = session?.taskProgress?.pets?.answers || {};
      await updateTaskProgress('pets', {
        ...currentAnswers,
        petsAndDependants: {
          pets: nextPets,
          dependants: nextDependents,
        },
      });
    } catch (error) {
      console.error('[Frame248] Failed to persist pets task:', error);
    }
  };

  useEffect(() => {
    if (session?.taskProgress?.pets?.answers) {
      const answers = session.taskProgress.pets.answers;

      if (answers.petsAndDependants) {
        setPets(normalizePets(answers.petsAndDependants.pets || []));
        setDependents(normalizeDependents(answers.petsAndDependants.dependants || []));
      }

      if (answers.financialArrangements) {
        setFinancialArrangements(answers.financialArrangements);
      }

      if (answers.finalInstructions) {
        setFinalInstructions(answers.finalInstructions);
      }

      return;
    }

    try {
      const backupData = localStorage.getItem('pets-dependants-backup');
      if (backupData) {
        const data = JSON.parse(backupData);
        if (data) {
          setPets(normalizePets(data.pets || []));
          setDependents(normalizeDependents(data.dependants || []));
        }
      }
    } catch (error) {
      console.error('[Frame248] Failed to load from backup:', error);
    }
  }, [session?.taskProgress?.pets]);

  const handleEdit = (section: string) => {
    switch (section) {
      case 'pets':
        navigate('/frame-242');
        break;
      case 'dependents':
        navigate('/frame-249');
        break;
      case 'financial':
        navigate('/frame-243');
        break;
      case 'final':
        navigate('/frame-244-pets');
        break;
      default:
        break;
    }
  };

  const handleRemovePetDocument = async (petId: string, documentIndex: number) => {
    const pet = pets.find((entry) => entry.id === petId);
    const docToRemove = pet?.documents?.[documentIndex];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame248] Failed to delete pet document:', error);
      alert('Failed to delete document.');
      return;
    }

    const updatedPets = pets.map((entry) => {
      if (entry.id !== petId) return entry;
      return {
        ...entry,
        documents: normalizeDocuments(entry.documents || []).filter(
          (_, index) => index !== documentIndex
        ),
      };
    });

    setPets(updatedPets);
    savePetsDependantsBackup(updatedPets, dependents);
    await persistPetsTask(updatedPets, dependents);
  };

  const handleRemoveDependentDocument = async (
    dependentId: string,
    documentIndex: number
  ) => {
    const dependent = dependents.find((entry) => entry.id === dependentId);
    const docToRemove = dependent?.documents?.[documentIndex];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame248] Failed to delete dependent document:', error);
      alert('Failed to delete document.');
      return;
    }

    const updatedDependents = dependents.map((entry) => {
      if (entry.id !== dependentId) return entry;
      return {
        ...entry,
        documents: normalizeDocuments(entry.documents || []).filter(
          (_, index) => index !== documentIndex
        ),
      };
    });

    setDependents(updatedDependents);
    savePetsDependantsBackup(pets, updatedDependents);
    await persistPetsTask(pets, updatedDependents);
  };

  const togglePetExpanded = (petId: string) => {
    setExpandedPets((prev) => ({
      ...prev,
      [petId]: !prev[petId],
    }));
  };

  const toggleDependentExpanded = (dependentId: string) => {
    setExpandedDependents((prev) => ({
      ...prev,
      [dependentId]: !prev[dependentId],
    }));
  };

  const capitalize = (str: string) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const hasValue = (value?: string | null) => {
    return Boolean(value && value.trim());
  };

  const renderDetailField = (label: string, value?: string | null) => {
    if (!hasValue(value)) return null;

    return (
      <div>
        <p className="text-sm font-medium text-stone-800">{label}</p>
        <p className="text-sm text-stone-600 whitespace-pre-wrap break-words">{value}</p>
      </div>
    );
  };

  const renderDocumentList = (
    ownerId: string,
    documents: AppDocument[],
    onRemove: (ownerId: string, documentIndex: number) => void
  ) => {
    if (documents.length === 0) return null;

    return (
      <div>
        <p className="text-sm font-medium text-stone-800 mb-2">
          Uploaded Documents ({documents.length})
        </p>

        <div className="space-y-2">
          {documents.map((doc, docIndex) => (
            <div
              key={`${ownerId}-doc-${doc.id}-${docIndex}`}
              className="flex flex-col gap-3 rounded-md border border-stone-200 bg-white px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <button
                type="button"
                onClick={() => openDocument(doc)}
                className="flex items-start gap-2 min-w-0 w-full text-left sm:flex-1"
              >
                <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                <span
                  className="text-sm font-medium text-stone-800 truncate min-w-0"
                  title={doc.name}
                >
                  {doc.name}
                </span>
              </button>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:shrink-0">
                <button
                  type="button"
                  onClick={() => downloadDocument(doc)}
                  className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                >
                  Download
                </button>

                <button
                  type="button"
                  onClick={() => onRemove(ownerId, docIndex)}
                  className="text-red-500 hover:text-red-700 text-xs shrink-0"
                  title="Remove document"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const detailsButtonClass =
    'px-3 py-1 text-sm font-medium text-cyan-700 border-2 border-cyan-700 rounded-md hover:border-cyan-600 hover:text-cyan-900 hover:bg-cyan-50 transition-colors whitespace-nowrap';

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="space-y-8">
          <ResponsiveHeader title="Pets and Dependents Care Plan Summary" />

          <Card>
            <h3 className="text-lg font-semibold text-stone-800 mb-4">
              Care Plan Overview
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="text-center border-r border-stone-100">
                <div className="text-2xl font-bold text-cyan-900">{pets.length}</div>
                <div className="text-sm text-stone-600 font-medium">Pets Entered</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-green-700">
                  {dependents.length}
                </div>
                <div className="text-sm text-stone-600 font-medium">
                  Dependents Entered
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-stone-800">Pets</h3>
                <span className="text-sm text-stone-500">({pets.length})</span>
              </div>

              <Button variant="outline" size="sm" onClick={() => handleEdit('pets')}>
                Edit
              </Button>
            </div>

            <div
              className={
                pets.length >= 3
                  ? 'max-h-[400px] overflow-y-auto pr-2 custom-scrollbar'
                  : ''
              }
            >
              {pets.length > 0 ? (
                <div className="space-y-3">
                  {pets.map((pet) => {
                    const attachedDocs = normalizeDocuments(pet.documents || []);
                    const isExpanded = !!expandedPets[pet.id];

                    const hasPetCareContacts =
                      hasValue(pet.veterinarian) ||
                      hasValue(pet.veterinarianContact) ||
                      hasValue(pet.preferredCaregiver) ||
                      hasValue(pet.caregiverContact) ||
                      hasValue(pet.emergencyContact) ||
                      hasValue(pet.emergencyContactPhone);

                    const hasPetCareInstructions =
                      hasValue(pet.diet) ||
                      hasValue(pet.medications) ||
                      hasValue(pet.careInstructions);

                    return (
                      <div key={pet.id} className="bg-stone-100 rounded-lg p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <div className="font-medium text-stone-800 break-words">
                              {pet.name}
                            </div>

                            <div className="text-sm text-stone-600 mt-1 break-words">
                              {pet.age && `Age: ${pet.age}`}
                              {pet.breed && ` • ${pet.breed}`}
                            </div>
                          </div>

                          <div className="flex items-center gap-3 sm:justify-end">
                            {pet.type && (
                              <span className="text-sm text-stone-700">
                                {capitalize(pet.type)}
                              </span>
                            )}

                            <button
                              type="button"
                              onClick={() => togglePetExpanded(pet.id)}
                              className={detailsButtonClass}
                            >
                              {isExpanded ? 'View Less' : 'View More'}
                            </button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-stone-200">
                            <div className="max-h-[320px] overflow-y-auto pr-2 custom-scrollbar">
                              <div className="space-y-4">
                                <div>
                                  <h4 className="text-sm font-semibold text-stone-800 mb-3">
                                    Pet Details
                                  </h4>

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {renderDetailField('Name', pet.name)}
                                    {renderDetailField('Type', capitalize(pet.type))}
                                    {renderDetailField('Breed', pet.breed)}
                                    {renderDetailField('Age', pet.age)}
                                    {renderDetailField('Special Needs', pet.specialNeeds)}
                                  </div>
                                </div>

                                {hasPetCareContacts && (
                                  <div>
                                    <h4 className="text-sm font-semibold text-stone-800 mb-3">
                                      Care Contacts
                                    </h4>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {renderDetailField('Veterinarian', pet.veterinarian)}
                                      {renderDetailField(
                                        'Contact Phone',
                                        pet.veterinarianContact
                                      )}
                                      {renderDetailField(
                                        'Preferred Caregiver',
                                        pet.preferredCaregiver
                                      )}
                                      {renderDetailField(
                                        'Caregiver Contact',
                                        pet.caregiverContact
                                      )}
                                      {renderDetailField(
                                        'Emergency Contact',
                                        pet.emergencyContact
                                      )}
                                      {renderDetailField(
                                        'Emergency Contact Phone',
                                        pet.emergencyContactPhone
                                      )}
                                    </div>
                                  </div>
                                )}

                                {hasPetCareInstructions && (
                                  <div>
                                    <h4 className="text-sm font-semibold text-stone-800 mb-3">
                                      Care Instructions
                                    </h4>

                                    <div className="space-y-4">
                                      {renderDetailField('Diet', pet.diet)}
                                      {renderDetailField(
                                        'Pet Medications',
                                        pet.medications
                                      )}
                                      {renderDetailField(
                                        'Care Instructions',
                                        pet.careInstructions
                                      )}
                                    </div>
                                  </div>
                                )}

                                {renderDocumentList(
                                  pet.id,
                                  attachedDocs,
                                  handleRemovePetDocument
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p>No pets added yet.</p>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/frame-242')}
                    className="mt-2"
                  >
                    Add Pets
                  </Button>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-stone-800">Dependents</h3>
                <span className="text-sm text-stone-500">({dependents.length})</span>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEdit('dependents')}
              >
                Edit
              </Button>
            </div>

            <div
              className={
                dependents.length >= 3
                  ? 'max-h-[400px] overflow-y-auto pr-2 custom-scrollbar'
                  : ''
              }
            >
              {dependents.length > 0 ? (
                <div className="space-y-3">
                  {dependents.map((dependent) => {
                    const attachedDocs = normalizeDocuments(dependent.documents || []);
                    const isExpanded = !!expandedDependents[dependent.id];

                    const hasMedicalInformation =
                      hasValue(dependent.medicalConditions) ||
                      hasValue(dependent.medications);

                    const hasDependentCareContacts =
                      hasValue(dependent.preferredGuardian) ||
                      hasValue(dependent.guardianContact) ||
                      hasValue(dependent.emergencyContact) ||
                      hasValue(dependent.emergencyContactPhone);

                    const hasDependentCareInstructions = hasValue(
                      dependent.careInstructions
                    );

                    return (
                      <div key={dependent.id} className="bg-stone-100 rounded-lg p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <div className="font-medium text-stone-800 break-words">
                              {dependent.name}
                            </div>

                            <div className="text-sm text-stone-600 mt-1 break-words">
                              {dependent.age && `Age: ${dependent.age}`}
                              {dependent.preferredGuardian &&
                                ` • Guardian: ${dependent.preferredGuardian}`}
                            </div>
                          </div>

                          <div className="flex items-center gap-3 sm:justify-end">
                            {dependent.relationship && (
                              <span className="text-sm text-stone-700">
                                {capitalize(dependent.relationship)}
                              </span>
                            )}

                            <button
                              type="button"
                              onClick={() => toggleDependentExpanded(dependent.id)}
                              className={detailsButtonClass}
                            >
                              {isExpanded ? 'View Less' : 'View More'}
                            </button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-stone-200">
                            <div className="max-h-[320px] overflow-y-auto pr-2 custom-scrollbar">
                              <div className="space-y-4">
                                <div>
                                  <h4 className="text-sm font-semibold text-stone-800 mb-3">
                                    Dependent Details
                                  </h4>

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {renderDetailField('Name', dependent.name)}
                                    {renderDetailField(
                                      'Relationship',
                                      capitalize(dependent.relationship)
                                    )}
                                    {renderDetailField('Age', dependent.age)}
                                    {renderDetailField(
                                      'Special Needs',
                                      dependent.specialNeeds
                                    )}
                                  </div>
                                </div>

                                {hasMedicalInformation && (
                                  <div>
                                    <h4 className="text-sm font-semibold text-stone-800 mb-3">
                                      Medical Information
                                    </h4>

                                    <div className="space-y-4">
                                      {renderDetailField(
                                        'Medical Conditions',
                                        dependent.medicalConditions
                                      )}
                                      {renderDetailField(
                                        'Medications',
                                        dependent.medications
                                      )}
                                    </div>
                                  </div>
                                )}

                                {hasDependentCareContacts && (
                                  <div>
                                    <h4 className="text-sm font-semibold text-stone-800 mb-3">
                                      Care Contacts
                                    </h4>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {renderDetailField(
                                        'Preferred Guardian',
                                        dependent.preferredGuardian
                                      )}
                                      {renderDetailField(
                                        'Guardian Contact',
                                        dependent.guardianContact
                                      )}
                                      {renderDetailField(
                                        'Emergency Contact',
                                        dependent.emergencyContact
                                      )}
                                      {renderDetailField(
                                        'Emergency Contact Phone',
                                        dependent.emergencyContactPhone
                                      )}
                                    </div>
                                  </div>
                                )}

                                {hasDependentCareInstructions && (
                                  <div>
                                    <h4 className="text-sm font-semibold text-stone-800 mb-3">
                                      Care Instructions
                                    </h4>

                                    {renderDetailField(
                                      'Care Instructions',
                                      dependent.careInstructions
                                    )}
                                  </div>
                                )}

                                {renderDocumentList(
                                  dependent.id,
                                  attachedDocs,
                                  handleRemoveDependentDocument
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p>No dependents added yet.</p>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/frame-249')}
                    className="mt-2"
                  >
                    Add Dependents
                  </Button>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-stone-800">
                Financial Arrangements
              </h2>
              <Button variant="outline" size="sm" onClick={() => handleEdit('financial')}>
                Edit
              </Button>
            </div>

            <div className="max-h-[150px] overflow-y-auto pr-2 custom-scrollbar">
              {financialArrangements?.petCareBudget?.trim() ||
              financialArrangements?.dependentCareBudget?.trim() ||
              financialArrangements?.trustFunds?.trim() ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-2 text-sm text-stone-600">
                    {financialArrangements.petCareBudget?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Pet Care Budget:
                        </span>{' '}
                        {financialArrangements.petCareBudget}
                      </p>
                    )}

                    {financialArrangements.petCareInstructions?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Pet Care Instructions:
                        </span>{' '}
                        {financialArrangements.petCareInstructions}
                      </p>
                    )}

                    {financialArrangements.dependentCareBudget?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Dependent Care Budget:
                        </span>{' '}
                        {financialArrangements.dependentCareBudget}
                      </p>
                    )}

                    {financialArrangements.dependentCareInstructions?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Dependent Care Instructions:
                        </span>{' '}
                        {financialArrangements.dependentCareInstructions}
                      </p>
                    )}

                    {financialArrangements.trustFunds?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Trust Funds:
                        </span>{' '}
                        {financialArrangements.trustFunds}
                      </p>
                    )}

                    {financialArrangements.insurancePolicies?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Insurance Policies:
                        </span>{' '}
                        {financialArrangements.insurancePolicies}
                      </p>
                    )}

                    {financialArrangements.emergencyFunds?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Emergency Funds:
                        </span>{' '}
                        {financialArrangements.emergencyFunds}
                      </p>
                    )}

                    {financialArrangements.paymentSchedule?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Payment Schedule:
                        </span>{' '}
                        {financialArrangements.paymentSchedule}
                      </p>
                    )}

                    {financialArrangements.careCoordinator?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Care Coordinator:
                        </span>{' '}
                        {financialArrangements.careCoordinator}{' '}
                        {financialArrangements.careCoordinatorContact
                          ? `(${financialArrangements.careCoordinatorContact})`
                          : ''}
                      </p>
                    )}

                    {financialArrangements.specialInstructions?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Special Instructions:
                        </span>{' '}
                        {financialArrangements.specialInstructions}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p className="mb-4">No financial arrangements added</p>
                  <Button variant="outline" onClick={() => navigate('/frame-243')}>
                    Add Financial Details
                  </Button>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-stone-800">
                Final Instructions
              </h2>
              <Button variant="outline" size="sm" onClick={() => handleEdit('final')}>
                Edit
              </Button>
            </div>

            <div className="max-h-[150px] overflow-y-auto pr-2 custom-scrollbar">
              {finalInstructions?.emergencyProcedures?.trim() ||
              finalInstructions?.documentLocations?.trim() ||
              finalInstructions?.importantContacts?.trim() ||
              finalInstructions?.finalMessage?.trim() ||
              finalInstructions?.specialRequests?.trim() ? (
                <div className="space-y-4 bg-stone-50 rounded-lg p-4">
                  {finalInstructions.emergencyProcedures?.trim() && (
                    <div>
                      <p className="font-medium text-stone-800 text-sm">
                        Emergency Procedures
                      </p>
                      <p className="text-sm text-stone-600">
                        {finalInstructions.emergencyProcedures}
                      </p>
                    </div>
                  )}

                  {finalInstructions.documentLocations?.trim() && (
                    <div>
                      <p className="font-medium text-stone-800 text-sm">
                        Document Locations
                      </p>
                      <p className="text-sm text-stone-600">
                        {finalInstructions.documentLocations}
                      </p>
                    </div>
                  )}

                  {finalInstructions.importantContacts?.trim() && (
                    <div>
                      <p className="font-medium text-stone-800 text-sm">
                        Important Contacts
                      </p>
                      <p className="text-sm text-stone-600">
                        {finalInstructions.importantContacts}
                      </p>
                    </div>
                  )}

                  {finalInstructions.finalMessage?.trim() && (
                    <div>
                      <p className="font-medium text-stone-800 text-sm">
                        Final Message
                      </p>
                      <p className="text-sm text-stone-600">
                        {finalInstructions.finalMessage}
                      </p>
                    </div>
                  )}

                  {finalInstructions.specialRequests?.trim() && (
                    <div>
                      <p className="font-medium text-stone-800 text-sm">
                        Special Requests
                      </p>
                      <p className="text-sm text-stone-600">
                        {finalInstructions.specialRequests}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p className="mb-4">No final instructions added</p>
                  <Button variant="outline" onClick={() => navigate('/frame-244-pets')}>
                    Add Instructions
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Plan for Your Pets and Dependents"
        subtitle="Review and confirm your care plan"
        progressPercent={100}
        onBack={() => navigate('/frame-244-pets')}
        backText="Back"
        nextNode={
          <FinishFlowAction
            flowId="pets"
            onFinished={() => navigate('/frame-140')}
            buttonText="Continue to Dashboard"
            className="!bg-cyan-700 hover:!bg-cyan-800 rounded-sm"
          />
        }
      />
    </ResponsiveContainer>
  );
};

export default Frame248;